import React, { useMemo, useState } from 'react';
import { PlayCircle, Clock, TrendingUp, ArrowUpRight, ArrowDownRight, ChevronDown, ChevronUp } from 'lucide-react';
import clsx from 'clsx';
import type { DauReportsResponse } from '../../types/reports';
import { KPICardSkeleton } from '../ui/Skeleton';
import { EmptyValue } from '../ui/EmptyValue';

interface ContentEngagementProps {
  dauData: DauReportsResponse | null;
  comparisonData?: DauReportsResponse | null;
  loading?: boolean;
}

/** Format milliseconds into a human-readable duration string */
const formatDuration = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

interface ContentMetrics {
  totalViews: number;
  totalDurationMs: number;
  comparisonViews: number | null;
  comparisonDurationMs: number | null;
  viewsTrend: number | null;
  durationTrend: number | null;
}

const useContentMetrics = (
  dauData: DauReportsResponse | null,
  comparisonData?: DauReportsResponse | null
): ContentMetrics | null => {
  return useMemo(() => {
    if (!dauData) return null;

    const totalViews = dauData.data.reduce(
      (sum, d) => sum + (d.FavoriteSessionsStarted ?? 0),
      0
    );
    const totalDurationMs = dauData.data.reduce(
      (sum, d) => sum + (d.FavoriteDurationMs ?? 0),
      0
    );

    let comparisonViews: number | null = null;
    let comparisonDurationMs: number | null = null;

    if (comparisonData && comparisonData.data.length > 0) {
      comparisonViews = comparisonData.data.reduce(
        (sum, d) => sum + (d.FavoriteSessionsStarted ?? 0),
        0
      );
      comparisonDurationMs = comparisonData.data.reduce(
        (sum, d) => sum + (d.FavoriteDurationMs ?? 0),
        0
      );
    }

    const viewsTrend =
      comparisonViews !== null && comparisonViews > 0
        ? ((totalViews - comparisonViews) / comparisonViews) * 100
        : null;

    const durationTrend =
      comparisonDurationMs !== null && comparisonDurationMs > 0
        ? ((totalDurationMs - comparisonDurationMs) / comparisonDurationMs) * 100
        : null;

    return {
      totalViews,
      totalDurationMs,
      comparisonViews,
      comparisonDurationMs,
      viewsTrend,
      durationTrend,
    };
  }, [dauData, comparisonData]);
};

const ContentEngagement: React.FC<ContentEngagementProps> = ({
  dauData,
  comparisonData,
  loading = false,
}) => {
  const metrics = useContentMetrics(dauData, comparisonData);
  const topResources = dauData?.summary?.topViewedResources;
  const hasResources = topResources && topResources.length > 0;
  const [expanded, setExpanded] = useState(false);

  const COLLAPSED_COUNT = 5;
  const EXPANDED_COUNT = 20;
  const visibleResources = hasResources
    ? topResources.slice(0, expanded ? EXPANDED_COUNT : COLLAPSED_COUNT)
    : [];
  const canExpand = hasResources && topResources.length > COLLAPSED_COUNT;

  // Skeleton
  if (loading) {
    return (
      <div className="rounded-card border border-rm-neutral-600/20 bg-white p-6 shadow-soft">
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-full bg-rm-accent-300/20 p-2 text-rm-primary-700">
            <PlayCircle className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold text-rm-primary-700">Content Engagement</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <KPICardSkeleton />
          <KPICardSkeleton />
        </div>
      </div>
    );
  }

  // Nothing to show
  if (!dauData) {
    return null;
  }

  return (
    <div className="rounded-card border border-rm-neutral-600/20 bg-white p-6 shadow-soft">
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-full bg-rm-accent-300/20 p-2 text-rm-primary-700">
          <PlayCircle className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-semibold text-rm-primary-700">Content Engagement</h2>
      </div>

      {/* KPI cards */}
      {metrics && (
        <div className={clsx(hasResources && 'mb-6', 'grid grid-cols-1 gap-4 sm:grid-cols-2')}>
          {/* Content Views */}
          <div className="flex items-center justify-between rounded-card bg-rm-bg-50 p-4">
            <div className="flex items-center gap-3">
              <PlayCircle className="h-5 w-5 text-rm-primary-600" />
              <div>
                <p className="text-sm font-medium text-rm-ink-900">Content Views</p>
                <p className="text-xs text-rm-neutral-600">Number of times content was viewed</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-semibold text-rm-ink-900">
                {metrics.totalViews > 0 ? metrics.totalViews.toLocaleString() : <EmptyValue size="lg" />}
              </p>
              {metrics.viewsTrend !== null ? (
                <div
                  className={clsx(
                    'inline-flex items-center gap-1 text-xs font-semibold',
                    metrics.viewsTrend >= 0 ? 'text-emerald-600' : 'text-red-600',
                    metrics.viewsTrend === 0 && 'text-rm-neutral-600'
                  )}
                  title={`Previous: ${metrics.comparisonViews?.toLocaleString() ?? 'N/A'}`}
                >
                  {metrics.viewsTrend >= 0 ? (
                    <ArrowUpRight className="h-3 w-3" />
                  ) : (
                    <ArrowDownRight className="h-3 w-3" />
                  )}
                  {metrics.viewsTrend >= 0 ? '+' : ''}
                  {Math.abs(metrics.viewsTrend).toFixed(1)}%
                </div>
              ) : (
                <p className="text-xs text-rm-neutral-600 italic">No comparison</p>
              )}
            </div>
          </div>

          {/* Total Watch Time */}
          <div className="flex items-center justify-between rounded-card bg-rm-bg-50 p-4">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-rm-primary-600" />
              <div>
                <p className="text-sm font-medium text-rm-ink-900">Watch Time</p>
                <p className="text-xs text-rm-neutral-600">Total engagement duration</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-semibold text-rm-ink-900">
                {metrics.totalDurationMs > 0 ? formatDuration(metrics.totalDurationMs) : <EmptyValue size="lg" />}
              </p>
              {metrics.durationTrend !== null ? (
                <div
                  className={clsx(
                    'inline-flex items-center gap-1 text-xs font-semibold',
                    metrics.durationTrend >= 0 ? 'text-emerald-600' : 'text-red-600',
                    metrics.durationTrend === 0 && 'text-rm-neutral-600'
                  )}
                  title={`Previous: ${metrics.comparisonDurationMs != null ? formatDuration(metrics.comparisonDurationMs) : 'N/A'}`}
                >
                  {metrics.durationTrend >= 0 ? (
                    <ArrowUpRight className="h-3 w-3" />
                  ) : (
                    <ArrowDownRight className="h-3 w-3" />
                  )}
                  {metrics.durationTrend >= 0 ? '+' : ''}
                  {Math.abs(metrics.durationTrend).toFixed(1)}%
                </div>
              ) : (
                <p className="text-xs text-rm-neutral-600 italic">No comparison</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Top Viewed Resources */}
      {hasResources && (
        <div>
          <div className="mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-rm-primary-600" />
            <h3 className="text-sm font-semibold text-rm-ink-900">Most Popular Resources</h3>
          </div>
          <div className="overflow-hidden rounded-card border border-rm-neutral-600/10">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-rm-neutral-600/10 bg-rm-bg-50">
                  <th className="px-4 py-2.5 font-medium text-rm-neutral-600">#</th>
                  <th className="px-4 py-2.5 font-medium text-rm-neutral-600">Title</th>
                  <th className="px-4 py-2.5 font-medium text-rm-neutral-600">Type</th>
                  <th className="px-4 py-2.5 text-right font-medium text-rm-neutral-600">Views</th>
                  <th className="px-4 py-2.5 text-right font-medium text-rm-neutral-600">Watch Time</th>
                </tr>
              </thead>
              <tbody>
                {visibleResources.map((resource, idx) => (
                  <tr
                    key={resource.url}
                    className={clsx(
                      'transition-colors hover:bg-rm-bg-50',
                      idx < visibleResources.length - 1 && 'border-b border-rm-neutral-600/10'
                    )}
                  >
                    <td className="px-4 py-2.5 text-rm-neutral-600">{idx + 1}</td>
                    <td className="max-w-[240px] truncate px-4 py-2.5 font-medium text-rm-ink-900" title={resource.title}>
                      {resource.title}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex rounded-full bg-rm-accent-300/20 px-2 py-0.5 text-xs font-medium capitalize text-rm-primary-700">
                        {resource.kind}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-rm-ink-900">
                      {resource.sessionCount.toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-right text-rm-ink-900">
                      {formatDuration(resource.totalDurationMs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {canExpand && (
            <button
              onClick={() => setExpanded((prev) => !prev)}
              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-rm-primary-700 hover:text-rm-primary-900 transition-colors"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Show fewer
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Show more ({Math.min(topResources.length, EXPANDED_COUNT)} total)
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentEngagement;
